import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { publicLinkService } from '../services/publicLinkService';

interface PublicContextType {
  isPublic: boolean;
  token: string | null | undefined;
  linkInfo: any;
  publicData: any;
  isLoading: boolean;
  error: string | null;
  loadPublicData: () => Promise<void>;
}

export const PublicContext = createContext<PublicContextType | undefined>(undefined);

interface PublicProviderProps {
  children: ReactNode;
}

export const PublicProvider: React.FC<PublicProviderProps> = ({ children }) => {
  const { token } = useParams<{ token: string }>();
  const isPublic = window.location.pathname.includes('/public/');
  
  const [linkInfo, setLinkInfo] = useState<any>(null);
  const [publicData, setPublicData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPublicData = useCallback(async () => {
    if (!token || !isPublic) return;
    
    setIsLoading(true);
    setError(null);

    try {
      // Validar token
      const linkValidation = await publicLinkService.validatePublicToken(token);
      setLinkInfo(linkValidation);

      // Obter dados do dashboard público
      const dashboard = await publicLinkService.getPublicDashboard(token);
      setPublicData(dashboard);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar dados');
    } finally {
      setIsLoading(false);
    }
  }, [token, isPublic]);

  // Carregar dados automaticamente quando o token mudar
  useEffect(() => {
    if (token && isPublic) {
      loadPublicData();
    }
  }, [token, isPublic, loadPublicData]);

  const value: PublicContextType = {
    isPublic,
    token,
    linkInfo,
    publicData,
    isLoading,
    error,
    loadPublicData,
  };

  return (
    <PublicContext.Provider value={value}>
      {children}
    </PublicContext.Provider>
  );
};

export const usePublic = (): PublicContextType => {
  const context = useContext(PublicContext);
  if (context === undefined) {
    throw new Error('usePublic must be used within a PublicProvider');
  }
  return context;
};
